// Sample 탭용 더미 데이터. GET /report/:id/data 응답(DB row)과 동일한 형태.
// 시나리오: "TransBlue" 공정에서 crack 이 몰리고 AI 오판정이 많음 → 리포트가 이를 드러내야 함.
import { ReportInspModelRow, ReportLabelRow, ReportRawData, ReportTrainRow } from "../components/report/reportMetrics";

// 고정 시드 → 매번 같은 결과
let seed = 42;
const rand = () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};
const pick = <T,>(weights: [T, number][]): T => {
  const total = weights.reduce((s, [, w]) => s + w, 0);
  let r = rand() * total;
  for (const [v, w] of weights) if ((r -= w) <= 0) return v;
  return weights[weights.length - 1][0];
};

const CLASSES = ["dent", "bubble", "crack", "dirt", "scratch"];
const PROCESSES: { name: string; count: number; mix: [string, number][]; errorRate: number }[] = [
  { name: "Darkgreen", count: 260, mix: [["OK", 40], ["dent", 15], ["bubble", 25], ["crack", 5], ["dirt", 10], ["scratch", 5]], errorRate: 0.06 },
  { name: "TransBlue", count: 220, mix: [["OK", 25], ["dent", 5], ["bubble", 10], ["crack", 45], ["dirt", 5], ["scratch", 10]], errorRate: 0.22 },
  { name: "Clear", count: 180, mix: [["OK", 50], ["dent", 10], ["bubble", 10], ["crack", 5], ["dirt", 20], ["scratch", 5]], errorRate: 0.08 },
  { name: "Amber", count: 140, mix: [["OK", 45], ["dent", 25], ["bubble", 5], ["crack", 5], ["dirt", 5], ["scratch", 15]], errorRate: 0.1 },
];
const DATES = ["260812", "260905", "260921", "261008"];

const labels: ReportLabelRow[] = [];
for (const proc of PROCESSES) {
  for (let i = 0; i < proc.count; i++) {
    const gt = pick(proc.mix);
    const wrong = rand() < proc.errorRate;
    // 오판정 시: OK→NG(과검) 또는 NG→다른 NG 클래스. 미검(NG→OK)은 제품상 허용 불가 → 샘플에서 미발생
    const ai = !wrong ? gt : gt === "OK" ? pick(CLASSES.map((c) => [c, 1])) : pick(CLASSES.filter((c) => c !== gt).map((c) => [c, 1]));
    const unlabeled = rand() < 0.12;
    const date = DATES[Math.floor(rand() * DATES.length)];
    labels.push({
      id: labels.length + 1,
      path: `/data1/datasets/SkySoftGel/${date}/${proc.name}/${gt === "OK" ? "OK" : "NG"}/IMG_${String(i).padStart(4, "0")}.jpg`,
      label: unlabeled ? "" : gt,
      AILabel: ai,
      score: +(wrong ? 0.4 + rand() * 0.3 : 0.75 + rand() * 0.25).toFixed(4),
      recentLabel: unlabeled ? ai : gt,
      ai_update_at: `20${date.slice(0, 2)}-${date.slice(2, 4)}-${date.slice(4, 6)}T09:00:00.000Z`,
    });
  }
}

// 학습 회차별 결과(점점 개선, 4회차는 실패)
const ROUNDS = [
  { day: "2026-08-14", epoch: 3, imageCount: 120, status: "Finished", overkill: 0.142, missing: 0 },
  { day: "2026-08-22", epoch: 5, imageCount: 220, status: "Finished", overkill: 0.105, missing: 0 },
  { day: "2026-09-03", epoch: 5, imageCount: 310, status: "Finished", overkill: 0.083, missing: 0 },
  { day: "2026-09-10", epoch: 5, imageCount: 310, status: "Failed", overkill: null, missing: null },
  { day: "2026-09-18", epoch: 8, imageCount: 450, status: "Finished", overkill: 0.061, missing: 0 },
  { day: "2026-09-29", epoch: 8, imageCount: 560, status: "Finished", overkill: 0.048, missing: 0 },
  { day: "2026-10-09", epoch: 10, imageCount: 690, status: "Finished", overkill: 0.037, missing: 0 },
] as const;

const trainHistory: ReportTrainRow[] = [];
const inspModels: ReportInspModelRow[] = [];
ROUNDS.forEach((r, i) => {
  const uuid = `sample-trainlog-${i + 1}`;
  const start = new Date(`${r.day}T05:00:00.000Z`);
  const minutes = r.status === "Failed" ? 4 : r.epoch * 3 + Math.round(r.imageCount / 60);
  trainHistory.push({
    id: 1000 + i,
    iteration: i + 1,
    class_name: JSON.stringify(CLASSES),
    epoch: r.epoch,
    imageCount: r.imageCount,
    accuracy: null, // 분석(멀티클래스) 학습은 accuracy 를 기록하지 않음
    status: r.status,
    create_at: start.toISOString(),
    finish_at: new Date(start.getTime() + minutes * 60000).toISOString(),
    trainLog_uuid: uuid,
  });
  if (r.overkill !== null) {
    inspModels.push({
      id: 2000 + i,
      name: `SkySoftGel_round${i + 1}`,
      create_at: new Date(start.getTime() + minutes * 60000).toISOString(),
      trainLog_uuid: uuid,
      info: JSON.stringify({
        project_id: 0,
        trainHistory_id: 1000 + i,
        performance: { overkill: r.overkill, missing: r.missing, threshold: +(0.5 - i * 0.02).toFixed(2) },
        epoch: { current: r.epoch, total: r.epoch, num_train: r.imageCount * r.epoch },
      }),
    });
  }
});

export const reportSampleData: ReportRawData = {
  project: {
    id: 0,
    project_name: "SkySoftGel 외관 불량 분석",
    step: "분석",
    class: JSON.stringify(CLASSES),
    material_name: "SkySoftGel",
    create_at: "2026-08-10T00:00:00.000Z",
  },
  labels,
  trainHistory,
  inspModels,
};

// ---- 검사(step "검사") 샘플: validHistory.results(threshold 기준 OK/NG) 로 구성 ----
// 프로젝트 이미지 = 학습 리스트 OK 300장 + 검증 리스트 160장. 리포트는 검증 리스트만 사용해야 함.
const INSP_TH = 0.2574;
const inspLabels: ReportLabelRow[] = [];
const validItems: { path: string; label: string; score: number }[] = [];
const LOTS = ["B628AK011-scan-2026-07-15_071320", "B628AK012-scan-2026-07-15_072249", "B628AK012-scan-r-2026-07-15_072652"];
const addImage = (gt: "OK" | "NG", i: number, validate: boolean) => {
  const path = `/data1/datasets/Jackpot/${gt}-Z1/${LOTS[i % LOTS.length]}/${i}-${gt}-Z1.JPG`;
  inspLabels.push({ id: inspLabels.length + 1, path, label: gt, AILabel: "", score: 0, recentLabel: gt, ai_update_at: null });
  if (!validate) return;
  // OK 약 8% 과검, NG 는 전부 임계값 초과(미검 없음)
  const score = gt === "OK" ? (rand() < 0.08 ? INSP_TH + rand() * 0.5 : rand() * INSP_TH * 0.9) : INSP_TH + 0.01 + rand() * (0.99 - INSP_TH);
  validItems.push({ path, label: score > INSP_TH ? "NG" : "OK", score: +score.toFixed(6) });
};
for (let i = 0; i < 300; i++) addImage("OK", i, false);
for (let i = 300; i < 420; i++) addImage("OK", i, true);
for (let i = 0; i < 40; i++) addImage("NG", i, true);

export const reportInspectionSampleData: ReportRawData = {
  project: {
    id: 0,
    project_name: "Jackpot Z1 외관 검사",
    step: "검사",
    class: JSON.stringify(["OK", "NG"]),
    material_name: "Jackpot",
    create_at: "2026-07-15T00:00:00.000Z",
  },
  labels: inspLabels,
  trainHistory: trainHistory.slice(0, 3).map((t) => ({ ...t, class_name: '["OK","NG"]', trainLog_uuid: null })),
  inspModels: [],
  validHistories: [{ id: 109, finished_at: "2026-10-16T13:12:29.000Z", model_uuid: "32058fcf-sample", trainHistory_id: 94 }],
  validation: {
    id: 109,
    finished_at: "2026-10-16T13:12:29.000Z",
    model_uuid: "32058fcf-sample",
    trainHistory_id: 94,
    results: JSON.stringify({ items: validItems, threshold: INSP_TH }),
    model: { uuid: "32058fcf-sample", name: "Jackpot_Z1_round8", create_at: "2026-10-16T12:40:00.000Z", info: null },
  },
};
